import { createContext, use } from "react";
import type { JSX, ReactNode } from "react";
import type { ApiClient } from "./ApiClient";

const ApiClientContext = createContext<ApiClient | null>(null);

/** Properties of {@link ApiClientProvider}. */
export interface ApiClientProviderProps {
  /** The client created in the composition root. */
  readonly client: ApiClient;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Makes the API client available to hooks below (dependency injection instead of a module singleton).
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function ApiClientProvider(props: ApiClientProviderProps): JSX.Element {
  const provider = <ApiClientContext value={props.client}>{props.children}</ApiClientContext>;

  return provider;
}

/**
 * Returns the injected API client.
 *
 * @returns The API client.
 * @throws Error if no {@link ApiClientProvider} is present.
 */
export function useApiClient(): ApiClient {
  const client = use(ApiClientContext);

  if (client === null) {
    throw new Error("useApiClient must be used inside an ApiClientProvider.");
  }

  return client;
}
